/**
 * Debug utility system for Treedis applications
 * Provides toggleable logging with localStorage persistence
 */

import { getEnvironment } from './environment';

// Debug configuration
const DEBUG_STORAGE_KEY = 'treedis_debug_logs';
const LOG_PREFIX = '[TREEDIS-DEBUG]';

/**
 * Check if debug logging is enabled
 * @returns {boolean} true if debug logging is enabled
 */
export const isDebugEnabled = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  const stored = localStorage.getItem(DEBUG_STORAGE_KEY);
  return stored === 'true';
};

/**
 * Toggle debug logging on/off
 * @returns {boolean} new debug state
 */
export const toggleLogs = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  const currentState = isDebugEnabled();
  const newState = !currentState;
  
  localStorage.setItem(DEBUG_STORAGE_KEY, newState.toString());
  
  console.log(
    `${LOG_PREFIX} Debug logging ${newState ? 'ENABLED' : 'DISABLED'}`
  );
  
  return newState;
};

/**
 * Log debug information if debug mode is enabled
 * @param category - Category of the log (e.g., 'THEME', 'API', 'COMPONENT')
 * @param message - Main log message
 * @param data - Optional data to log
 */
export const debugLog = (
  category: string,
  message: string,
  data?: any
): void => {
  if (!isDebugEnabled()) {
    return;
  }

  const timestamp = new Date().toISOString().split('T')[1].split('.')[0];
  const environment = getEnvironment();
  
  const logMessage = `${LOG_PREFIX}[${category}][${timestamp}][${environment}] ${message}`;
  
  if (data !== undefined) {
    console.log(logMessage, data);
  } else {
    console.log(logMessage);
  }
};

/**
 * Create a theme fingerprint for comparison
 * @param theme - MUI theme object
 * @returns {string} theme fingerprint
 */
export const createThemeFingerprint = (theme: any): string => {
  if (!theme) return 'no-theme';
  
  const key = {
    direction: theme.direction,
    mode: theme.palette?.mode,
    primaryColor: theme.palette?.primary?.main,
    fontFamily: theme.typography?.fontFamily,
    borderRadius: theme.shape?.borderRadius,
  };
  
  return JSON.stringify(key);
};

/**
 * Initialize debug system and expose global toggle function
 */
export const initDebugSystem = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  // Expose toggle function globally
  (window as any).toggleLogs = toggleLogs;
  
  // Log initialization if debug is already enabled
  if (isDebugEnabled()) {
    debugLog('SYSTEM', 'Debug system initialized - logging enabled');
    console.log(`${LOG_PREFIX} Use window.toggleLogs() to toggle debug logging`);
  }
};

/**
 * Log theme-specific debug information
 */
export const themeDebugLog = {
  init: (language: string, direction: string) => {
    debugLog('THEME', `ThemeProvider initializing`, { language, direction });
  },
  
  created: (fingerprint: string, source: string) => {
    debugLog('THEME', `Theme created from ${source}`, { fingerprint });
  },
  
  applied: (component: string, fingerprint: string) => {
    debugLog('THEME', `Theme applied in ${component}`, { fingerprint });
  },
  
  verification: (expected: string, actual: string, match: boolean) => {
    debugLog('THEME', `Theme verification`, { 
      expected, 
      actual, 
      match,
      status: match ? '✅ MATCH' : '❌ MISMATCH'
    });
  }
};

export default {
  isDebugEnabled,
  toggleLogs,
  debugLog,
  createThemeFingerprint,
  initDebugSystem,
  themeDebugLog,
};
